import { useEffect, useMemo, useRef, useState } from 'react';
import {
  computeDisplayedPotentialWin,
  getDisplayedFinalOdds,
} from '../_helpers/betCartStakeLimits';
import { toDisplayOddsDecimal } from '../_helpers/oddsFormat';
import {
  normalizeSportsnewComboBonusPreview,
  resolveSportsnewDisplayedPotentialWin,
} from '../_helpers/sportsnewComboBonusPreview';
import {
  buildBetQuotePreviewPayload,
  buildBetQuotePreviewPayloadKey,
} from '../_helpers/betQuotePreviewPayload';
import { normalizeSportsnewPreviewFailure } from '../_helpers/betErrorResync';

const roundOdds = (value) => toDisplayOddsDecimal(value) ?? 0;

const toFiniteNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

export default function useBetQuotePreview({
  SportsService,
  selections,
  betType,
  bettingCategory,
  displayScope,
  stake,
  totalOdds,
  bonusMultiplier,
  bettingLimits,
  isAuthenticated,
  isSportsnew = false,
  betMode = 'prematch',
  enabled = true,
  requestOwnerKey = '',
}) {
  const [quote, setQuote] = useState(null);
  const [quoteKey, setQuoteKey] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [requestStateOwnerKey, setRequestStateOwnerKey] = useState('');
  const requestSeqRef = useRef(0);
  const normalizedRequestOwnerKey = String(requestOwnerKey || '');
  const previewKey = useMemo(() => {
    if (!enabled || !isAuthenticated || betType !== 'COMBO' || selections.length < 2) return '';
    return buildBetQuotePreviewPayloadKey(buildBetQuotePreviewPayload({
      selections,
      betType,
      bettingCategory,
      displayScope,
      isSportsnew,
      betMode,
    }));
  }, [enabled, selections, betType, bettingCategory, displayScope, isAuthenticated, isSportsnew, betMode]);
  const previewPayload = useMemo(
    // The key is the serialized normalized payload itself. Object-only cart
    // rerenders cannot restart it, and every payload fallback participates.
    () => previewKey ? JSON.parse(previewKey) : null,
    [previewKey],
  );

  useEffect(() => {
    if (!enabled || !previewKey || !previewPayload || !SportsService?.previewBet) {
      setRequestStateOwnerKey(normalizedRequestOwnerKey);
      setQuote(null);
      setQuoteKey('');
      setQuoteLoading(false);
      setQuoteError(null);
      return undefined;
    }

    const seq = ++requestSeqRef.current;
    const ownerKey = normalizedRequestOwnerKey;
    setRequestStateOwnerKey(ownerKey);
    setQuote(null);
    setQuoteKey('');
    setQuoteLoading(true);
    setQuoteError(null);

    const request = async () => {
      try {
        const response = await SportsService.previewBet(previewPayload);
        if (seq !== requestSeqRef.current) return;
        if (response?.status && response?.data) {
          setQuote(response.data);
          setQuoteKey(previewKey);
          setQuoteError(null);
        } else {
          setQuote(null);
          setQuoteKey('');
          setQuoteError(normalizeSportsnewPreviewFailure({
            previewKey,
            source: response,
            fallbackCode: 'PREVIEW_REJECTED',
          }));
        }
      } catch (error) {
        if (seq === requestSeqRef.current) {
          setQuote(null);
          setQuoteKey('');
          setQuoteError(normalizeSportsnewPreviewFailure({
            previewKey,
            source: error,
            fallbackCode: 'PREVIEW_UNAVAILABLE',
          }));
        }
      } finally {
        if (seq === requestSeqRef.current) {
          setQuoteLoading(false);
        }
      }
    };

    request();

    return () => {
      if (requestSeqRef.current === seq) requestSeqRef.current += 1;
    };
  }, [SportsService, enabled, normalizedRequestOwnerKey, previewKey, previewPayload]);

  const ownsRequestState = requestStateOwnerKey === normalizedRequestOwnerKey;
  const ownedQuote = ownsRequestState ? quote : null;
  const ownedQuoteKey = ownsRequestState ? quoteKey : '';
  const ownedQuoteError = ownsRequestState ? quoteError : null;
  const ownedQuoteLoading = ownsRequestState ? quoteLoading : false;

  const normalizedSportsnewQuote = useMemo(
    () => isSportsnew && ownedQuoteKey === previewKey
      ? normalizeSportsnewComboBonusPreview(ownedQuote)
      : null,
    [isSportsnew, ownedQuote, ownedQuoteKey, previewKey]
  );
  const displayedBaseOdds = useMemo(() => {
    const quotedBaseOdds = normalizedSportsnewQuote?.baseOdds
      ?? toFiniteNumber(ownedQuote?.base_odds, 0);
    if (quotedBaseOdds > 0) {
      return roundOdds(quotedBaseOdds);
    }
    return roundOdds(totalOdds || 0);
  }, [normalizedSportsnewQuote?.baseOdds, ownedQuote?.base_odds, totalOdds]);
  const displayedBonusMultiplier = useMemo(() => {
    if (betType !== 'COMBO') return 1;
    if (isSportsnew && !normalizedSportsnewQuote) return 1;
    return roundOdds(normalizedSportsnewQuote?.bonusMultiplier
      ?? toFiniteNumber(ownedQuote?.bonus_multiplier, bonusMultiplier || 1));
  }, [betType, isSportsnew, normalizedSportsnewQuote, ownedQuote?.bonus_multiplier, bonusMultiplier]);
  const comboOddsAdjust = useMemo(
    () => roundOdds(normalizedSportsnewQuote?.comboOddsAdjust
      ?? toFiniteNumber(ownedQuote?.combo_odds_adjust, 0)),
    [normalizedSportsnewQuote?.comboOddsAdjust, ownedQuote?.combo_odds_adjust]
  );

  const displayedFinalOdds = useMemo(() => {
    if (betType === 'COMBO') {
      const quotedFinalOdds = normalizedSportsnewQuote?.totalOdds
        ?? toFiniteNumber(ownedQuote?.total_odds, 0);
      if (quotedFinalOdds > 0) {
        return roundOdds(quotedFinalOdds);
      }
      if (isSportsnew) return displayedBaseOdds;
    }
    return getDisplayedFinalOdds({ betType, selections, totalOdds, bonusMultiplier });
  }, [betType, isSportsnew, normalizedSportsnewQuote?.totalOdds, ownedQuote?.total_odds, displayedBaseOdds, selections, totalOdds, bonusMultiplier]);

  const displayedPotentialWin = useMemo(() => {
    if (!isSportsnew) return computeDisplayedPotentialWin({
      stake,
      displayedFinalOdds,
      bettingLimits,
    });
    return resolveSportsnewDisplayedPotentialWin({
      normalizedQuote: normalizedSportsnewQuote,
      stake,
      displayedFinalOdds,
      bettingLimits,
    });
  }, [isSportsnew, normalizedSportsnewQuote, stake, displayedFinalOdds, bettingLimits]);

  const shouldShowFinalOdds = useMemo(() => {
    if (betType !== 'COMBO') return false;
    return (
      Math.abs(displayedFinalOdds - displayedBaseOdds) > 0.001
      || Math.abs(displayedBonusMultiplier - 1) > 0.001
      || Math.abs(comboOddsAdjust) > 0.001
      || ownedQuoteLoading
    );
  }, [
    betType,
    displayedFinalOdds,
    displayedBaseOdds,
    displayedBonusMultiplier,
    comboOddsAdjust,
    ownedQuoteLoading,
  ]);

  return {
    quote: ownedQuote,
    quoteLoading: ownedQuoteLoading,
    quoteError: ownedQuoteError,
    quoteErrorCode: ownedQuoteError?.code ?? null,
    previewKey,
    displayedBaseOdds,
    displayedBonusMultiplier,
    comboOddsAdjust,
    displayedFinalOdds,
    displayedPotentialWin,
    bonusEligibleLegCount: normalizedSportsnewQuote?.eligibleLegCount ?? null,
    bonusTierLegCount: normalizedSportsnewQuote?.tierLegCount ?? null,
    bonusEligibilityThreshold: normalizedSportsnewQuote?.threshold ?? null,
    bonusEligibilityMode: normalizedSportsnewQuote?.mode ?? null,
    authoritativeMinimumStake: normalizedSportsnewQuote?.policyMinimumStake ?? null,
    hasAuthoritativeSportsnewQuote: Boolean(normalizedSportsnewQuote),
    shouldShowFinalOdds,
  };
}
